// src/components/Sidebar.tsx
// Responsive sidebar — fixed on desktop, drawer overlay on mobile

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface SidebarProps {
  // No props needed - self-contained responsive sidebar
}

const Sidebar: React.FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close mobile drawer on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getIconPath = (baseName: string) => {
    return `/icons/${baseName}-${theme}.png`;
  };

  // v3.19.7: Starter is now the 6-day free trial. Workflow is Pro/Ultimate only.
  // Starter users see a lock icon on the Workflow nav item — clicking goes to /pay
  // with an upsell reason. Bookmate creation cap of 2 + no download/share for
  // Starter is enforced at the page level + backend 403.
  const tier = user?.subscription_tier;
  const isStarter = tier === 'starter';

  const navigationItems = [
    {
      id: 'codemate',
      iconBase: 'chat',
      label: 'Chat',
      onClick: () => {
        const lastSession = localStorage.getItem('lastCodemateSession');
        navigate(lastSession ? `/codemate/${lastSession}` : '/codemate');
      },
    },
    {
      id: 'bookmate',
      iconBase: 'bookmate',
      label: 'Bookmate',
      path: '/bookmate',
    },
    {
      id: 'mlmate',
      iconBase: 'mlmate',
      label: 'MLmate',
      path: '/mlmate',
    },
    {
      id: 'paths',
      iconBase: 'path',
      label: 'Paths',
      path: '/paths',
    },
    {
      id: 'capstone',
      iconBase: 'projects',
      label: 'Capstone',
      path: '/capstone',
    },
    {
      id: 'workflow',
      iconBase: 'workflow',
      label: 'Workflow',
      locked: isStarter,
      onClick: () => {
        if (isStarter) {
          navigate('/pay?reason=workflow_requires_pro');
          return;
        }
        const lastSession = localStorage.getItem('lastWorkflowSession');
        navigate(lastSession ? `/workflow/${lastSession}` : '/workflow');
      },
    },
    {
      id: 'community',
      iconBase: 'community',
      label: 'Community',
      path: '/guide',
    },
  ];

  const bottomItems = [
    {
      id: 'history',
      iconBase: 'history',
      label: 'History',
      path: '/codemate/history',
    },
    {
      id: 'settings',
      iconBase: 'settings',
      label: 'Settings',
      path: '/settings',
    },
  ];

  const isActive = (id: string) => {
    const path = location.pathname;
    switch (id) {
      case 'codemate': return path.startsWith('/codemate') && !path.includes('/history');
      case 'bookmate': return path.startsWith('/bookmate');
      case 'workflow': return path.startsWith('/workflow');
      case 'community': return path.startsWith('/guide');
      case 'capstone': return path.startsWith('/capstone');
      case 'mlmate': return path.startsWith('/mlmate');
      case 'paths': return path.startsWith('/paths');
      case 'history': return path === '/codemate/history';
      case 'settings': return path === '/settings';
      default: return false;
    }
  };

  const sidebarContent = (
    <div className="h-full bg-gray-50 dark:bg-gray-950 flex flex-col w-[72px] border-r border-gray-200 dark:border-gray-800">
      {/* Top Navigation Items */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navigationItems.map((item) => {
          const isComingSoon = 'comingSoon' in item && item.comingSoon;
          const isLocked = 'locked' in item && item.locked;
          const isDisabled = isComingSoon;

          return (
            <button
              key={item.id}
              onClick={isDisabled ? undefined : (item.onClick || (() => 'path' in item && item.path && navigate(item.path)))}
              className={`w-full flex flex-col items-center justify-center py-2.5 px-1 rounded-lg transition-all relative ${
                isDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : isActive(item.id)
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                    : 'text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-900'
              }`}
              title={isComingSoon ? 'Coming in next phase' : (isLocked ? 'Upgrade to Pro to unlock' : undefined)}
            >
              <>
                <div className="relative">
                  <img
                    src={getIconPath(item.iconBase)}
                    alt={item.label}
                    className={`w-5 h-5 mb-0.5 ${isLocked ? 'opacity-50' : ''}`}
                  />
                  {isLocked && (
                    <span className="absolute -top-1 -right-1 text-[10px]" title="Pro/Ultimate only">🔒</span>
                  )}
                </div>
                <span className={`text-[10px] font-medium text-center leading-tight ${isLocked ? 'opacity-60' : ''}`}>{item.label}</span>
                {isComingSoon && (
                  <span className="text-[7px] text-gray-400 dark:text-gray-500 leading-tight">Next Phase</span>
                )}
              </>
            </button>
          );
        })}
      </nav>

      {/* Bottom Navigation Items */}
      <div className="border-t border-gray-200 dark:border-gray-800 py-3 px-2 space-y-1">
        {bottomItems.map((item) => (
          <button
            key={item.id}
            onClick={() => item.path && navigate(item.path)}
            className={`w-full flex flex-col items-center justify-center py-2.5 px-1 rounded-lg transition-all ${
              isActive(item.id)
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                : 'text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-900'
            }`}
          >
            <img
              src={getIconPath(item.iconBase)}
              alt={item.label}
              className="w-5 h-5 mb-0.5"
            />
            <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
          </button>
        ))}

        {/* User Profile */}
        <div className="relative pt-2">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-full flex flex-col items-center justify-center py-2 px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-all"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-semibold text-xs">
              {user?.email?.substring(0, 1).toUpperCase() || 'S'}
            </div>
          </button>

          {/* User Menu Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute left-full ml-2 bottom-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 min-w-[180px]">
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.email || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{
                      user?.subscription_tier === 'ultimate' ? 'Ultimate plan'
                      : user?.subscription_tier === 'pro' ? 'Pro plan'
                      : user?.subscription_tier === 'starter' ? 'Starter plan'
                      : user?.subscription_tier?.startsWith('ltd_') ? 'Lifetime plan'
                      : 'No plan'
                    }</p>
                  </div>
                  {/* v3.19.7: visible Upgrade button for Starter + Pro users */}
                  {(user?.subscription_tier === 'starter' || user?.subscription_tier === 'pro') && (
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/pay');
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm font-semibold text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-200 dark:border-gray-700"
                    >
                      <span>⬆</span>
                      <span>Upgrade</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                      window.location.href = '/login';
                    }}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span>↪</span>
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger button — fixed top-left */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 w-9 h-9 flex items-center justify-center rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 text-foreground shadow-sm"
        aria-label="Open menu"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Desktop sidebar — always visible */}
      <div className="hidden md:block flex-shrink-0">
        {sidebarContent}
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="md:hidden fixed inset-y-0 left-0 z-50 animate-slide-in-left">
            {sidebarContent}
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
